import { Form, Formik } from 'formik';
import React, { useContext, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';

import Confirm from '../../../components/confirm';
import ConfirmSelect from '../../../components/confirm-select';
import SaveBackdrop from '../../../components/saveBackdrop';
import Wait from '../../../components/wait';
import { AppContext } from '../../../contexts/app-context';
import GlobalStyles from '../../../global-styles';
import API from '../../../utils/api';
import {
  apiReponseError, apiRequestError, canReadCompany, canReadKit, canReadProgram, canReadSKU,
  canWriteSKU, compareArrayObjectValue, formatDateForDataGrid, generateQRCode, getErrorObject,
  getSKUAssociateEntities, getSKUDetail, goToRoutePage, isFormDataValid
} from '../../../utils/helper';
import {
  AddIcon, ChevronRightIcon, DeleteIcon, GetAppToneIcon, OpenInNewIcon
} from '../../../utils/icons';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel,
  FormGroup, FormHelperText, FormLabel, Grid, IconButton, InputLabel, Link, List, ListItem,
  ListItemSecondaryAction, ListItemText, Menu, MenuItem, Select, Switch, TextField, Tooltip,
  Typography
} from '../../../utils/material-ui-core';

interface skuCRUDProps {
  open: boolean;
  skuKey: string;
  onCallback: (confirmOutput: ConfirmOutput) => void;
}

// Default form value
const formDefaultValue = {
  isDevice: false,
  fulfillable: false,
  kitCount: 0,
  lastModifiedBy: "",
  lastModifiedDateTime: "",
  name: "",
  nodeReferenceName: "",
  partNumber: "",
  skuKey: "",
  vendorName: "",
  vendorKey: "",
  enrollmentCount: 0,
};

const formDefaultTouchValue = {
  isDevice: false,
  fulfillable: false,
  kitCount: false,
  name: false,
  nodeReferenceName: false,
  partNumber: false,
  skuKey: false,
  vendorName: false,
  vendorKey: false,
};

// Form validation schema
const formValidationSchema = Yup.object().shape({
  skuKey: Yup.string().required("SKU key is required."),
  partNumber: Yup.string().required("SKU is required."),
  name: Yup.string().required("Name is required."),
  vendorKey: Yup.string().required("Platform vendor is required"),
  fulfillable: Yup.boolean().required("Allow To Be Part of a Kit is required"),
  nodeReferenceName: Yup.string()
    .trim()
    .when(["isDevice"], {
      is: (isDevice) => {
        return isDevice;
      },
      then: Yup.string().required("Node name is required."),
    }),
});

const newSKUDefaultValue = {
  isDevice: false,
  fulfillable: false,
  kitCount: 0,
  name: "",
  nodeReferenceName: "",
  partNumber: "",
  skuKey: "",
  vendorName: "",
  vendorKey: "",
};

const SKUCRUD = ({ open, skuKey, onCallback }: skuCRUDProps) => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const formReference = useRef(null) as any;

  const { refreshData, referenceLookups, showAlert, userFunctionAccess } = useContext(AppContext);

  const [anchorElForAssociateEntityMenu, setAnchorElForAssociateEntityMenu] = useState<null | HTMLElement>(null);

  const [qrcodeBase64String, setQrcodeBase64String] = useState<string>("");

  const [loading, setLoading] = useState<boolean>(true);
  const [readonly, setReadonly] = useState<boolean>(true);
  const [limitedUpdates, setLimitedUpdates] = useState<boolean>(false);
  const [isNew, setIsNew] = useState<boolean>(true);
  const [saveButtonVisible, setSaveButtonVisible] = useState<boolean>(false);
  const [initialFormValues, setInitialFormValues] = useState<SKU>(formDefaultValue);
  const [deleteConfirmDialogOpenForSKU, setDeleteConfirmDialogOpenForSKU] = useState<boolean>(false);
  const [associatedKits, setAssociatedKits] = useState<AssociatedKit[]>([]);
  const [associatedEntities, setAssociatedCompanies] = useState<AssociatedEntity[]>([]);
  const [selectedAssociatedEntity, setSelectedAssociatedEntity] = useState({
    arrayIndex: 0,
    associatedEntity: { entityKey: "", name: "", type: "" },
  });
  const [deleteConfirmDialogOpenForAssociatedEntity, setDeleteConfirmDialogOpenForAssociatedEntity] = useState<boolean>(false);
  const [selectAssociateEntityDialogOpen, setSelectAssociateEntityDialogOpen] = useState<boolean>(false);
  const [newAssociateEntityLookupValues, setNewAssociateEntityLookupValues] = useState<Lookup[]>([]);
  const [associateType, setAssociateType] = useState<string>("");
  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);

  // Association Entities

  const hasAssociateEntityAccess = (type: string) => {
    if (type.toString().toLowerCase() === "company" && canReadCompany(userFunctionAccess)) {
      return true;
    }

    if (type.toString().toLowerCase() === "program" && canReadProgram(userFunctionAccess)) {
      return true;
    }
    return false;
  };

  const handleAnchorElAssociateMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElForAssociateEntityMenu(event.currentTarget);
  };

  const handleAnchorElAssociateMenuClose = () => {
    setAnchorElForAssociateEntityMenu(null);
  };

  const delsertAssociateEntity = async (entityKey: string, mode: string) => {
    const payload = {};
    try {
      if (mode === "deleted") {
        await API.delete("/api/onboarding/skus/" + skuKey + "/associate/entities/" + entityKey)
          .then(async (response) => {
            showAlert({
              message: "Successfully " + mode + ".",
              severity: "success",
            });
          })
          .catch((error) => {
            showAlert({
              message: apiReponseError(error, "Failed to save sku associate entity."),
              severity: "error",
            });
          });
      } else {
        await API.post("/api/onboarding/skus/" + skuKey + "/associate/entities/" + entityKey, payload)
          .then(async (response) => {
            showAlert({
              message: "Successfully " + mode + ".",
              severity: "success",
            });
          })
          .catch((error) => {
            showAlert({
              message: apiReponseError(error, "Failed to save sku associate entity."),
              severity: "error",
            });
          });
      }
    } catch (e) {
      showAlert({
        message: apiRequestError(e, "Something was wrong in our system."),
        severity: "error",
      });
    }
  };

  const showDeleteConfirmationForAssociateEntity = (arrayIndex: number, props: AssociatedEntity) => {
    setSelectedAssociatedEntity({
      arrayIndex: arrayIndex,
      associatedEntity: props,
    });
    setDeleteConfirmDialogOpenForAssociatedEntity(true);
  };

  const deleteConfirmationForAssociateEntity = async (deleteConfirm: boolean) => {
    setDeleteConfirmDialogOpenForAssociatedEntity(false);
    if (deleteConfirm) {
      setLoading(true);
      await delsertAssociateEntity(selectedAssociatedEntity.associatedEntity.entityKey, "deleted");
      setTimeout(async () => {
        await refreshAssociateEntities();
      }, 300);
    }
  };

  const refreshAssociateEntities = async () => {
    const apiResponse = await getSKUAssociateEntities(skuKey);
    if (apiResponse.success) {
      const data = apiResponse.data;
      setAssociatedCompanies(data.associatedEntities as AssociatedEntity[]);
      setLoading(false);
    } else {
      showAlert({ message: apiResponse.errorMessage, severity: "error" });
      cancel();
      return;
    }
  };

  const saveNewAssociateEntities = async (confirmOutput: ConfirmOutput) => {
    setSelectAssociateEntityDialogOpen(false);

    if (confirmOutput.status === "select") {
      setLoading(true);
      confirmOutput.data.forEach(async (d) => {
        await delsertAssociateEntity(d.value, "added");
      });
      setTimeout(async () => {
        await refreshAssociateEntities();
      }, 300);
    }
  };

  const handleAssociateEntitySelection = (type: string) => {
    const lookupValues: Lookup[] = [];
    let lookupExists = false;
    setNewAssociateEntityLookupValues(lookupValues);
    setAnchorElForAssociateEntityMenu(null);
    setAssociateType(type);

    if (type.toLowerCase() === "company") {
      for (let i = 0; i < referenceLookups.clientCompanies.length; i++) {
        const key = referenceLookups.clientCompanies[i].clientCompanyKey;
        if (associatedEntities.findIndex((Entity) => Entity.entityKey.toLowerCase() === key.toLowerCase()) === -1) {
          lookupValues.push({
            value: key,
            name: referenceLookups.clientCompanies[i].name,
            checked: false,
          });
        }
      }
      lookupExists = true;
    } else if (type.toLowerCase() === "program") {
      for (let i = 0; i < referenceLookups.programs.length; i++) {
        const key = referenceLookups.programs[i].programKey;
        if (associatedEntities.findIndex((Entity) => Entity.entityKey.toLowerCase() === key.toLowerCase()) === -1) {
          lookupValues.push({
            value: key,
            name: referenceLookups.programs[i].programName,
            checked: false,
          });
        }
      }
      lookupExists = true;
    }

    if (lookupExists) {
      setNewAssociateEntityLookupValues(lookupValues.sort(compareArrayObjectValue("name", "asc")));
      setSelectAssociateEntityDialogOpen(true);
    }
  };

  const gotoEntity = (associatedEntity: AssociatedEntity) => {
    if (associatedEntity.type.toString().toLowerCase() === "company") {
      goToRoutePage("onboarding/clientcompanies/" + associatedEntity.entityKey);
    } else if (associatedEntity.type.toString().toLowerCase() === "program") {
      goToRoutePage("onboarding/programs/" + associatedEntity.entityKey);
    }
  };

  // SKU

  const setQRCode = async (text: string) => {
    const apiResponse2 = await generateQRCode(text);
    if (apiResponse2.success) {
      const data2 = apiResponse2.data;
      setQrcodeBase64String(data2.qrcodeBase64String);
    } else {
      setQrcodeBase64String("");
    }
  };

  const showDeleteConfirmationDialog = () => {
    setDeleteConfirmDialogOpenForSKU(true);
  };

  const deleteSKU = async (confirm: boolean) => {
    setDeleteConfirmDialogOpenForSKU(false);

    if (confirm) {
      setSaveInProgress(true);
      try {
        await API.delete("/api/onboarding/skus/" + skuKey)
          .then(async (response) => {
            showAlert({
              message: "Successfully removed.",
              severity: "success",
            });
            refreshData("skus");
            onCallback({ status: "success", data: "" });
          })
          .catch((error) => {
            showAlert({
              message: apiReponseError(error, "Failed to delete sku data."),
              severity: "error",
            });
            setSaveInProgress(false);
          });
      } catch (e) {
        showAlert({
          message: apiRequestError(e, "Something was wrong in our system."),
          severity: "error",
        });
        setSaveInProgress(false);
      }
    }
  };

  const saveSKU = async () => {
    if (!canWriteSKU(userFunctionAccess)) {
      showAlert({ message: "Access Denied.", severity: "error" });
      cancel();
      return;
    }

    const payload = {
      sku: {
        fulfillable: formReference.current.values.fulfillable,
        skuKey: formReference.current.values.skuKey,
        name: formReference.current.values.name,
        partNumber: formReference.current.values.partNumber,
        vendorKey: formReference.current.values.vendorKey,
        nodeReferenceName: formReference.current.values.isDevice ? formReference.current.values.nodeReferenceName : "",
      },
    };

    try {
      setSaveInProgress(true);
      await API.post("/api/onboarding/skus/" + formReference.current.values.skuKey, payload)
        .then(async (response) => {
          showAlert({ message: "Successfully saved.", severity: "success" });
          await refreshData("skus");
          onCallback({ status: "success", data: "" });
        })
        .catch((error) => {
          setSaveInProgress(false);
          const errorList: ApiResponseErrorObject[] = getErrorObject(error, "sku.");
          let isErrorAssignedAtFieldLevel = true;
          try {
            errorList.forEach(function (error) {
              formReference.current.setFieldError(error.title, error.message.join(" "));
            });
          } catch (e) {
            isErrorAssignedAtFieldLevel = false;
          }
          isErrorAssignedAtFieldLevel = false;
          if (errorList.length <= 0 || !isErrorAssignedAtFieldLevel) {
            showAlert({
              message: apiReponseError(error, "Failed to save sku data."),
              severity: "error",
            });
          }
        });
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something was wrong in our system."),
        severity: "error",
      });
    }
  };

  const cancelSKU = () => {
    formReference.current.resetForm({});
    onCallback({ status: "cancel", data: "" });
  };

  const gotoKit = (kitKey: string) => {
    goToRoutePage("onboarding/kits/" + kitKey);
  };

  // Other
  const cancel = () => {
    onCallback({ status: "cancel", data: "" });
  };

  useEffect(() => {
    const initialPageAsync = async () => {
      if (open) {
        setLoading(true);
        setReadonly(true);
        setSaveButtonVisible(false);

        if (isFormDataValid(formReference)) {
          formReference.current.resetForm({});
        }

        if (!canReadSKU(userFunctionAccess)) {
          showAlert({ message: "Access Denied.", severity: "error" });
          cancel();
          return;
        }

        if (skuKey === "") {
          if (!canWriteSKU(userFunctionAccess)) {
            showAlert({ message: "Access Denied.", severity: "error" });
            cancel();
            return;
          }

          let defaultVendorKey = "";
          const filterVendors = referenceLookups.vendors.filter((vendor) => vendor.name === "Meshify UI");
          if (filterVendors.length === 1) {
            defaultVendorKey = filterVendors[0].vendorKey;
          }

          setInitialFormValues({
            ...newSKUDefaultValue,
            skuKey: referenceLookups.newRecordDefaultId,
            vendorKey: defaultVendorKey,
            fulfillable: true,
          } as SKU);
          setAssociatedKits([] as AssociatedKit[]);
          setAssociatedCompanies([] as AssociatedEntity[]);
          setIsNew(true);
          setReadonly(false);
          setLimitedUpdates(false);
          setLoading(false);
        } else {
          const apiResponse = await getSKUDetail(skuKey);
          if (apiResponse.success) {
            const data = apiResponse.data;
            setInitialFormValues(data.sku as SKU);
            setAssociatedKits(data.associatedKits as AssociatedKit[]);
            setAssociatedCompanies(data.associatedEntities as AssociatedEntity[]);
            setIsNew(false);
            setReadonly(!canWriteSKU(userFunctionAccess));
            // if (apiResponse.data.sku.enrollmentCount > 0) {
            //   setLimitedUpdates(true);
            // } else {
            //   setLimitedUpdates(false);
            // }
            setLimitedUpdates(false);
            setLoading(false);

            setQRCode(data.sku.partNumber);
          } else {
            showAlert({ message: apiResponse.errorMessage, severity: "error" });
            cancel();
            return;
          }
        }
      }
    };

    initialPageAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, skuKey]);

  return (
    <>
      {formReference.current !== null ? (
        <>
          <Confirm title="Confirmation" content={`<p>Are you sure you want to delete this SKU?</p><p style="text-align:center"><b>${formReference.current.values.name} (${formReference.current.values.partNumber})</b></p><p style="text-align:center"></p>`} yesText="DELETE" noText="CANCEL" open={deleteConfirmDialogOpenForSKU} callbackOnClose={() => setDeleteConfirmDialogOpenForSKU} callbackOnConfirm={deleteSKU} />

          <Confirm title="Confirmation" content={`<p>Are you sure you want to remove the <b>${selectedAssociatedEntity.associatedEntity.type.toLowerCase()}</b> association from this SKU?</p><p style="text-align:center"><b>${selectedAssociatedEntity.associatedEntity.name}</b></p>`} yesText="REMOVE" noText="CANCEL" open={deleteConfirmDialogOpenForAssociatedEntity} callbackOnClose={() => { }} callbackOnConfirm={deleteConfirmationForAssociateEntity} />

          <ConfirmSelect title={`Select Associated ${associateType}`} label={"Kit"} content={``} data={newAssociateEntityLookupValues} yesText="SELECT" noText="CANCEL" open={selectAssociateEntityDialogOpen} multiSelect={true} maxSelect={5} callbackOnSelect={saveNewAssociateEntities} />
        </>
      ) : null}

      <Dialog className={globalStyles.dialogContainer} fullWidth={true} open={open} maxWidth={"md"}>
        <DialogTitle className={globalStyles.dialogTitleContainer}>
          <Typography variant="subtitle1" component="div">
            {readonly ? "View SKU" : isNew ? "New SKU" : "Edit SKU"}
          </Typography>
        </DialogTitle>
        {loading ? (
          <>
            <DialogContent dividers>
              <Wait />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => cancel()} color="secondary" autoFocus>
                <u>Cancel</u>
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogContent dividers className={globalStyles.formikContainer}>
              <SaveBackdrop open={saveInProgress} />

              {limitedUpdates ? (
                <Grid container>
                  <Grid item xs={12}>
                    {limitedUpdates ? <Alert severity="info">{"Please note that limited updates can be made to this sku since " + initialFormValues.enrollmentCount + " kits have already been enrolled."}</Alert> : null}
                    <Box p={3}></Box>
                  </Grid>
                </Grid>
              ) : null}

              <Formik
                innerRef={formReference}
                initialValues={initialFormValues}
                validationSchema={formValidationSchema}
                validateOnMount={true}
                initialTouched={formDefaultTouchValue}
                validateOnBlur={true}
                validateOnChange={true}
                onSubmit={(values: SKU) => {
                  saveSKU();
                }}
              >
                {({ values, errors, handleChange, handleSubmit, touched, handleBlur, isSubmitting, setFieldValue, setFieldError, resetForm }) => (
                  <Form
                    onChange={() => {
                      setSaveButtonVisible(true);
                    }}
                  >
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        {
                          isNew ?
                            <Box display="flex" justifyContent="flex-end">
                              <Link
                                variant="body2"
                                href={"./SKUCreationGuide.pdf"}
                                onClick={() => {
                                  showAlert({
                                    message: "Opening SKU Creation Guide",
                                    severity: "info",
                                  });
                                }}
                                color="secondary"
                                download
                                target="blank"
                              >
                                <Box display="flex" justifyContent="Center">
                                  <Box component="span">SKU Creation Guide&nbsp;</Box>
                                  <Box component="span">
                                    <OpenInNewIcon fontSize="small" />
                                  </Box>
                                </Box>
                              </Link>
                            </Box>
                            : null
                        }
                        {/* <Box p={1}>&nbsp;</Box> */}
                      </Grid>


                      <Grid item xs={12} sm={6} md={6} lg={6}>
                        <FormControl required disabled={readonly || limitedUpdates} className={globalStyles.formControl} fullWidth={true}>
                          <InputLabel id="platform-vendor-id-label" shrink={true}>
                            Platform Vendor
                          </InputLabel>
                          <Select native labelId="platform-vendor-id-label" id="vendorKey" name="vendorKey" value={values.vendorKey} onChange={handleChange} label="Ownership">
                            {referenceLookups.vendors
                              .filter((vendor) => vendor.name.toLowerCase() !== "shopify")
                              .map((vendor, index) => (
                                <option value={vendor.vendorKey} key={index}>
                                  {vendor.name}
                                </option>
                              ))}
                          </Select>
                          <FormHelperText error>{touched.vendorKey && Boolean(errors.vendorKey) ? errors.vendorKey : ""}</FormHelperText>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} sm={6} md={6} lg={6}>
                        <TextField
                          disabled={readonly || limitedUpdates}
                          id="partNumber"
                          name="partNumber"
                          label="SKU"
                          value={values.partNumber}
                          onChange={(e) => {
                            handleChange(e);
                            setQRCode(e.target.value);
                          }}
                          onBlur={handleBlur}
                          error={touched.partNumber && Boolean(errors.partNumber)}
                          helperText={errors.partNumber && touched.partNumber ? errors.partNumber : null}
                        />
                      </Grid>

                      <Grid item xs={12} sm={12} md={6} lg={6}>
                        <TextField disabled={readonly || limitedUpdates} id="name" name="name" label="Name" value={values.name} onChange={handleChange} onBlur={handleBlur} error={touched.name && Boolean(errors.name)} helperText={errors.name && touched.name ? errors.name : null} />
                      </Grid>

                      <Grid item xs={12} sm={4} md={2} lg={2}>
                        <FormControl className={globalStyles.formControl} fullWidth={true}>
                          <FormLabel component="legend" className={globalStyles.checkBoxLabel}>
                            Is Device
                          </FormLabel>
                          <FormGroup>
                            <FormControlLabel
                              control={
                                <Switch
                                  disabled={readonly || limitedUpdates}
                                  id="isDevice"
                                  name="isDevice"
                                  checked={values.isDevice}
                                  onChange={(ev, checked) => {
                                    setFieldValue("isDevice", checked);
                                  }}
                                />
                              }
                              label={values.isDevice ? "Yes" : "No"}
                              labelPlacement="end"
                            />
                          </FormGroup>
                        </FormControl>
                      </Grid>

                      {values.isDevice ? (
                        <Grid item xs={12} sm={4} md={4} lg={4}>
                          <TextField disabled={readonly || limitedUpdates} id="nodeReferenceName" name="nodeReferenceName" label="Node Name" value={values.nodeReferenceName} onChange={handleChange} onBlur={handleBlur} error={touched.nodeReferenceName && Boolean(errors.nodeReferenceName)} helperText={errors.nodeReferenceName && touched.nodeReferenceName ? errors.nodeReferenceName : null} />
                        </Grid>
                      ) : null}

                      <Grid item xs={12} sm={4} md={6} lg={6}>
                        <FormControl className={globalStyles.formControl} fullWidth={true}>
                          <FormLabel component="legend" className={globalStyles.checkBoxLabel}>
                            Allow To Be Part of a Kit
                          </FormLabel>
                          <FormGroup>
                            <FormControlLabel
                              control={
                                <Switch
                                  disabled={readonly || limitedUpdates}
                                  id="fulfillable"
                                  name="fulfillable"
                                  checked={values.fulfillable}
                                  onChange={(ev, checked) => {
                                    setFieldValue("fulfillable", checked);
                                  }}
                                />
                              }
                              label={values.fulfillable ? "Yes" : "No"}
                              labelPlacement="end"
                            />
                          </FormGroup>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Form>
                )}
              </Formik>

              {!isNew ? (
                <>
                  <Box p={1}>&nbsp;</Box>
                  <Grid container>
                    <Grid item xs={12} className={globalStyles.topic}>
                      <Grid container>
                        <Grid item xs={8}>
                          ASSOCIATED ENTITIES
                        </Grid>
                        <Grid item xs={4}>
                          {!readonly && !saveButtonVisible ? (
                            <Box display="flex" justifyContent="flex-end" width={1} onClick={handleAnchorElAssociateMenuClick}>
                              <Tooltip arrow title="Add associate company">
                                <AddIcon color="secondary" />
                              </Tooltip>
                            </Box>
                          ) : null}
                          <Menu
                            id="associate-entity-menu"
                            anchorEl={anchorElForAssociateEntityMenu}
                            keepMounted
                            open={Boolean(anchorElForAssociateEntityMenu)}
                            onClose={handleAnchorElAssociateMenuClose}
                            getContentAnchorEl={null}
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "center",
                            }}
                            transformOrigin={{
                              vertical: "top",
                              horizontal: "center",
                            }}
                          >
                            <MenuItem
                              onClick={() => {
                                handleAssociateEntitySelection("Company");
                              }}
                            >
                              Company
                            </MenuItem>
                            <MenuItem
                              onClick={() => {
                                handleAssociateEntitySelection("Program");
                              }}
                            >
                              Program
                            </MenuItem>
                          </Menu>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <Box className={pageStyles.contentText}>
                        {associatedEntities
                          .map((item) => item.type)
                          .filter((value, index, self) => self.indexOf(value) === index)
                          .map((value, index0, self) => (
                            <Box key={index0}>
                              <Typography variant="body2" className={pageStyles.associatedEntiteHeader}>
                                {value}
                              </Typography>
                              <List>
                                {associatedEntities
                                  .filter((associatedEntity) => associatedEntity.type === value)
                                  .map((associatedEntity, index) => (
                                    <React.Fragment key={index}>
                                      {!readonly && !saveButtonVisible && hasAssociateEntityAccess(associatedEntity.type) ? (
                                        <>
                                          <ListItem button onClick={() => gotoEntity(associatedEntity)}>
                                            <ListItemText primary={associatedEntity.name} />
                                            <ListItemSecondaryAction>
                                              <IconButton edge="end" onClick={() => showDeleteConfirmationForAssociateEntity(index, associatedEntity)}>
                                                <DeleteIcon color="error" />
                                              </IconButton>
                                              <IconButton edge="end" onClick={() => gotoEntity(associatedEntity)}>
                                                <ChevronRightIcon />
                                              </IconButton>
                                            </ListItemSecondaryAction>
                                          </ListItem>
                                        </>
                                      ) : !readonly && !saveButtonVisible ? (
                                        <ListItem button>
                                          <ListItemText primary={associatedEntity.name} />

                                          <ListItemSecondaryAction>
                                            <IconButton edge="end" onClick={() => showDeleteConfirmationForAssociateEntity(index, associatedEntity)}>
                                              <DeleteIcon color="error" />
                                            </IconButton>
                                            <IconButton edge="end"></IconButton>
                                          </ListItemSecondaryAction>
                                        </ListItem>
                                      ) : (
                                        <ListItem>
                                          <ListItemText primary={associatedEntity.name} />
                                          <ListItemSecondaryAction>
                                            {hasAssociateEntityAccess(associatedEntity.type) && !saveButtonVisible ? (
                                              <IconButton edge="end" onClick={() => gotoEntity(associatedEntity)}>
                                                <ChevronRightIcon />
                                              </IconButton>
                                            ) : (
                                              <IconButton edge="end"></IconButton>
                                            )}
                                          </ListItemSecondaryAction>
                                        </ListItem>
                                      )}
                                    </React.Fragment>
                                  ))}
                              </List>
                            </Box>
                          ))}
                      </Box>
                    </Grid>
                  </Grid>

                  <Box p={1}>&nbsp;</Box>

                  <Grid container>
                    <Grid item xs={12}>
                      <Box className={globalStyles.topic}>ASSOCIATED KITS</Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box className={pageStyles.contentText}>
                        <List>
                          {associatedKits.map((kit, index) => (
                            <React.Fragment key={index}>
                              {canReadKit(userFunctionAccess) && !saveButtonVisible ? (
                                <ListItem button key={index} onClick={() => gotoKit(kit.kitKey)}>
                                  <ListItemText primary={kit.name} />
                                  <ListItemSecondaryAction>
                                    <IconButton edge="end">
                                      <ChevronRightIcon />
                                    </IconButton>
                                  </ListItemSecondaryAction>
                                </ListItem>
                              ) : (
                                <ListItem key={index}>
                                  <ListItemText primary={kit.name} />
                                </ListItem>
                              )}
                            </React.Fragment>
                          ))}
                        </List>
                      </Box>
                    </Grid>
                  </Grid>
                </>
              ) : null}

              {!isNew ? (
                <>
                  <Box p={1}>&nbsp;</Box>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Box className={globalStyles.topic}>Audit</Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={6} lg={6}>
                      <TextField disabled={true} label="Last Modified By" value={initialFormValues.lastModifiedBy} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6} lg={6}>
                      <TextField disabled={true} label="Last Modified On" value={formatDateForDataGrid(initialFormValues.lastModifiedDateTime)} />
                    </Grid>
                  </Grid>
                </>
              ) : null}

              {!isNew && qrcodeBase64String !== "" ? (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Box p={5}></Box>

                    <Box p={0} display="flex" justifyContent="center">
                      <img src={qrcodeBase64String} style={{ width: "150px", height: "150px" }} alt="QR Code" />
                    </Box>
                    <Box p={0} display="flex" justifyContent="center">
                      <Link href={qrcodeBase64String} variant="body2" download="qrcode.png">
                        <GetAppToneIcon fontSize="medium" color="secondary" />
                      </Link>
                    </Box>
                    <Box p={3}></Box>
                  </Grid>
                </Grid>
              ) : null}
            </DialogContent>
            <DialogActions>
              {!readonly && !saveButtonVisible && skuKey !== "" ? (
                <>
                  {associatedKits.length === 0 && associatedEntities.length === 0 ? (
                    <Button onClick={() => showDeleteConfirmationDialog()} className={globalStyles.errorText}>
                      <DeleteIcon color="error" />
                    </Button>
                  ) : null}
                </>
              ) : null}

              {!readonly ? (
                <Button disabled={!saveButtonVisible} onClick={() => formReference.current.handleSubmit()} color="secondary" variant="contained" className={globalStyles.buttonDefault}>
                  {isNew ? "Save" : "Update"}
                </Button>
              ) : null}

              <Button onClick={() => cancelSKU()} autoFocus className={globalStyles.buttonDefault}>
                <u>Cancel</u>
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    contentText: {
      maxHeight: "300px",
      overflow: "auto",
      // whiteSpace: 'pre-wrap',
      // minHeight: '400px',
    },
    associatedEntiteHeader: {
      fontWeight: "bold",
    },
  })
);

export default SKUCRUD;
